import { formatDateTimeWithTimezone } from "../../../../../utils/dateUtils";
import { ITasOccurrenceComment } from "../../../../../models/tasOccurrence";
import { useIntl } from "react-intl";

type ChatRowProps = {
  comment: ITasOccurrenceComment;
  isEditing: boolean;
  editDraft: string;
  onEditDraftChange: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  savingEdit: boolean;
  deleting: boolean;
  disabled: boolean | undefined;
};

export const ChatRow = ({
  comment,
  isEditing,
  editDraft,
  onEditDraftChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  savingEdit,
  deleting,
  disabled,
}: ChatRowProps) => {
  const intl = useIntl();

  const metaLine = [
    comment.commentedBy ?? "—",
    comment.createdAt ? formatDateTimeWithTimezone(comment.createdAt, intl.locale) : "",
  ]
    .filter(Boolean)
    .join(" - ");

  return (
    <div className="pt-4 first:pt-0 my-6">
      <div className="flex flex-row items-start justify-between gap-2 mb-2">
        <span className="text-size-14 text-color-status-resting">
          {metaLine}
          {comment.wasUpdated && <span className="text-size-14 text-color-info font-bold"> - {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.edited" })}</span>}
        </span>
        {comment.availableToEdit && !isEditing && !disabled && (
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              className="p-0 border-0 bg-transparent cursor-pointer"
              onClick={onStartEdit}
              aria-label={intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.edit" })}
            >
              <span className="rioglyph rioglyph-pencil text-color-primary text-size-18" />
            </button>
            <button
              type="button"
              className="p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50"
              onClick={onDelete}
              disabled={deleting}
              aria-label={intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.delete" })}
            >
              <span className="rioglyph rioglyph-trash text-color-gray text-size-18" />
            </button>
          </div>
        )}
      </div>

      {isEditing && !disabled ? (
        <div className="flex flex-col gap-3">
          <textarea
            className="form-control w-full min-h-[100px] resize-y mb-3"
            value={editDraft}
            onChange={(e) => onEditDraftChange(e.target.value)}
            disabled={savingEdit}
          />
          <div className="flex justify-end gap-8 items-center">
            <button
              type="button"
              className="btn btn-primary btn-link p-0"
              onClick={onCancelEdit}
              disabled={savingEdit}
            >
              {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.cancel" })}
            </button>
            <button
              type="button"
              className="btn border border-[#00A0B0] text-[#00A0B0] bg-white dark:bg-neutral-800 hover:bg-[#F9FDFD] dark:hover:bg-neutral-700 px-4 py-2 rounded disabled:opacity-50"
              onClick={onSaveEdit}
              disabled={savingEdit || !editDraft.trim()}
            >
              {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.edit-save" })}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#F9FAFB] dark:bg-neutral-800 rounded-md p-6 text-size-14 text-color-dark whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: comment.comment ?? "" }} />
      )}
    </div>
  );
};
